import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  useWinnerTakesAllList,
  useTournamentDetails,
} from "@/lib/hooks/useApi";
import { NavigationMatch } from "@/components/NavigationMatch";
import type { Tournament } from "@/lib/types/api";
import AnimatedLoading from "@/components/AnimatedLoading";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import { WinnerTakesAllView } from "@/components/WinnerTakesAllView";
import { DefaultButton } from "@/components/DefaultButton";
import { t } from "@/lib/i18n";

// Single WTA type — no more room selection (tiers consolidated to one).
const WTA_TYPE = "wta_one_per_day";

function WinnerTakesAll() {
  const router = useRouter();

  const { data: list, isLoading: isListLoading } = useWinnerTakesAllList();

  const filteredList = useMemo(() => {
    if (!list) return [];
    return list.filter((tournament) => tournament.tournamentType === WTA_TYPE);
  }, [list]);

  // Local state for the fallback view (rendered when there's no active
  // tournament — TeleportWidget normally bypasses this page entirely).
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>();
  const [hasTournament, setHasTournament] = useState<boolean>(true);
  const [showPlayersList, setShowPlayersList] = useState<boolean>(false);
  const { data: tournamentDetails, refetch: refetchTournamentDetails } =
    useTournamentDetails(selectedTournamentId ?? "");

  if (isListLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <AnimatedLoading />
      </div>
    );
  }

  // No active tournament — render the old WinnerTakesAllView fallback so
  // users still see the building art + "no tournament" empty state UI.
  return (
    <div className="flex flex-col w-full h-full relative overflow-hidden">
      <FloatingLeftButton
        onClick={() => router.navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <ResourceBar className="absolute top-4 left-14 right-4 flex z-50 pl-3" />

      <div className="absolute top-20 right-4 z-50 flex flex-col items-center gap-6">
        <WtaHistoryEntry />
      </div>

      <div className="rounded-lg flex-1 flex flex-col relative">
        <div className="max-h-[60svh] flex flex-col flex-1 min-h-0 relative">
          <img
            src="https://cdn.navalabs.io/assets/BUILDING_NoAyzL.png"
            alt="Arena"
            className="absolute inset-0 w-full h-auto object-cover z-0"
            style={{ imageRendering: "pixelated" }}
          />
          <div className="relative z-10 flex flex-col flex-1 min-h-0">
            <WinnerTakesAllView
              tournamentsList={filteredList}
              inView={true}
              onTournamentSelect={setSelectedTournamentId}
              onHasTournamentChange={setHasTournament}
              onShowPlayersList={() => setShowPlayersList(true)}
            />
          </div>
        </div>

        <NavigationMatch
          key={selectedTournamentId}
          tournamentId={selectedTournamentId ?? ""}
          tournamentDetails={tournamentDetails ?? ({} as Tournament)}
          hasTournament={hasTournament}
          entryAnalyticsMode="winner_takes_all"
          showPlayersList={showPlayersList}
          onBackToMatch={() => setShowPlayersList(false)}
          refetchTournamentDetails={() => refetchTournamentDetails()}
          className="h-full max-h-[40svh] z-50"
          isWinnerTakesAll={true}
        />
      </div>

      <img
        src="https://cdn.navalabs.io/assets/dark-varentino_pEKOvE.png"
        alt="Varentino"
        className="absolute bottom-[calc(40svh+12px)] left-2 w-16 h-16 object-contain z-50"
        style={{ imageRendering: "pixelated" }}
      />
    </div>
  );
}

function WtaHistoryEntry() {
  const navigate = useRouter().navigate;
  return (
    <DefaultButton
      onClick={() => navigate({ to: "/winner-takes-all/history" })}
      className="w-full flex flex-col items-center hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-95"
    >
      <div className="flex justify-center -mt-6">
        <svg
          className="size-8"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 22.0253V24L16 24V22.0253L8 22.0253Z" fill="black" />
          <path
            d="M4.0009 20.0506V22.0253L8 22.0253V20.0506H4.0009Z"
            fill="black"
          />
          <path d="M2 16V20.0506H4.0009V16L2 16Z" fill="black" />
          <path d="M16 22.0253H20V20.0506H16V22.0253Z" fill="black" />
          <path d="M20 20.0506H22V16H20V18.0253V20.0506Z" fill="black" />
          <path d="M22 16H24V8H22V14V16Z" fill="black" />
          <path d="M22 8V3.94937H20V8L22 8Z" fill="black" />
          <path d="M20 3.94937V1.97469L16 1.97468V3.94937H20Z" fill="black" />
          <path
            d="M8 1.90735e-06V1.97469V2L16 1.97468V0L8 1.90735e-06Z"
            fill="black"
          />
          <path
            d="M8 1.97469L4 1.97468V3.94937L6 3.94937L8 3.94937V2V1.97469Z"
            fill="black"
          />
          <path d="M4 3.94937H2V8L4 8V3.94937Z" fill="black" />
          <path d="M8 20.0506V22.0253L16 22.0253V20.0506H8Z" fill="#56C3FE" />
          <path d="M16 20.0506H20V18.0253H16V20.0506Z" fill="#56C3FE" />
          <path d="M20 16H22V14H20V16Z" fill="#56C3FE" />
          <path d="M4 3.94937V8H6V3.94937L4 3.94937Z" fill="#DBFDFF" />
          <path d="M16 3.94937V1.97468L8 2V3.94937H16Z" fill="#DBFDFF" />
          <path d="M2 8L0 8V16H2V8Z" fill="black" />
          <path d="M4 8L2 8V16L4.0009 16L4 8Z" fill="#DBFDFF" />
          <path d="M14 14H6V12H12V6H14V14Z" fill="black" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.0009 16V20.0506H8H16V18.0253H20V16V14H22V8L20 8V3.94937H16H8L6 3.94937V8H4L4.0009 16ZM6 14H14V6H12V12H6V14Z"
            fill="#55ECFF"
          />
        </svg>
      </div>
      <div
        className="flex flex-col items-center text-xs mt-2 leading-none uppercase max-w-10!"
        style={{
          textShadow: "black 0px 2px",
        }}
      >
        {t("winnerTakesAllView.viewHistory")}
      </div>
    </DefaultButton>
  );
}

export const Route = createFileRoute("/(game)/winner-takes-all/")({
  component: WinnerTakesAll,
});
